import { useEffect, useRef } from "react";
import { MeshStandardMaterial } from "three";
import type { Mesh, Object3D, Material } from "three";
import type {
  SceneIndexRefs,
  MeshWithOriginal,
  RoomMesh,
  OriginalMaterialProps,
} from "./types";
import { isMesh, classifyRoomMeshRole } from "./utils";

export function useSceneIndex(scene: Object3D | null): SceneIndexRefs {
  const allMeshesRef = useRef<Mesh[]>([]);
  const baseMaterialStateRef = useRef<WeakMap<Mesh, Material | Material[]>>(
    new WeakMap()
  );
  const originalMaterialPropsRef = useRef<
    WeakMap<MeshStandardMaterial, OriginalMaterialProps>
  >(new WeakMap());
  const floorMapRef = useRef<Map<string, MeshWithOriginal[]>>(new Map());
  const roomMapRef = useRef<Map<string, RoomMesh[]>>(new Map());
  const stairsMapRef = useRef<Map<string, MeshWithOriginal[]>>(new Map());
  const initializedRef = useRef(false);

  // Single traverse on GLB load - build indexes
  useEffect(() => {
    if (!scene || initializedRef.current) return;

    const allMeshes: Mesh[] = [];
    const baseMaterialState = new WeakMap<Mesh, Material | Material[]>();
    const originalMaterialProps = new WeakMap<
      MeshStandardMaterial,
      OriginalMaterialProps
    >();
    const floorMap = new Map<string, MeshWithOriginal[]>();
    const roomMap = new Map<string, RoomMesh[]>();
    const stairsMap = new Map<string, MeshWithOriginal[]>();

    const findAncestorBy = (
      node: Object3D | null,
      predicate: (name: string) => boolean
    ): Object3D | null => {
      let cur: Object3D | null = node;
      while (cur) {
        if (predicate(cur.name)) return cur;
        cur = cur.parent ?? null;
      }
      return null;
    };

    const saveMaterialProps = (mat: MeshStandardMaterial) => {
      if (!originalMaterialProps.has(mat)) {
        originalMaterialProps.set(mat, {
          material: mat.clone(),
        });
      }
    };

    scene.traverse((obj) => {
      if (!isMesh(obj)) return;
      const mesh = obj as Mesh;

      allMeshes.push(mesh);
      baseMaterialState.set(mesh, mesh.material);

      // Save original material properties
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((m) => {
          if (m instanceof MeshStandardMaterial) saveMaterialProps(m);
        });
      } else if (mesh.material instanceof MeshStandardMaterial) {
        saveMaterialProps(mesh.material);
      }

      // Skip points
      if (mesh.name.startsWith("point_")) {
        return;
      }

      // Floors
      const floorAncestor = findAncestorBy(obj, (name) =>
        name.toLowerCase().startsWith("floor_")
      );
      if (floorAncestor) {
        const id = floorAncestor.name;
        if (!floorMap.has(id)) floorMap.set(id, []);
        floorMap.get(id)!.push({ mesh, originalMaterial: mesh.material });
      }

      // Stairs
      const stairsAncestor = findAncestorBy(obj, (name) =>
        name.toLowerCase().startsWith("stairs_")
      );
      if (stairsAncestor) {
        const id = stairsAncestor.name;
        if (!stairsMap.has(id)) stairsMap.set(id, []);
        stairsMap.get(id)!.push({ mesh, originalMaterial: mesh.material });
      }

      // Rooms
      const roomAncestor = findAncestorBy(obj, (name) => {
        const n = name.toLowerCase();
        return (
          n.startsWith("room_") ||
          n === "room_library" ||
          n.startsWith("room_wardrobe")
        );
      });
      if (roomAncestor) {
        const roomId = roomAncestor.name;
        if (!roomMap.has(roomId)) roomMap.set(roomId, []);
        const role = classifyRoomMeshRole(mesh, roomAncestor);
        roomMap.get(roomId)!.push({
          mesh,
          originalMaterial: mesh.material,
          role,
        });
      }
    });

    allMeshesRef.current = allMeshes;
    baseMaterialStateRef.current = baseMaterialState;
    originalMaterialPropsRef.current = originalMaterialProps;
    floorMapRef.current = floorMap;
    roomMapRef.current = roomMap;
    stairsMapRef.current = stairsMap;
    initializedRef.current = true;
  }, [scene]);

  return {
    allMeshesRef,
    baseMaterialStateRef,
    originalMaterialPropsRef,
    floorMapRef,
    roomMapRef,
    stairsMapRef,
    initializedRef,
  };
}
